import React, {useEffect, useState} from "react";
import {Grid} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {Person, PersonRestControllerApiFactory, PersonSummary} from "arteco-api-client-ts";
import {AxiosResponse} from "axios";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {EmployeeDataFields} from "../../../../components/layout/shared/person/employee_data";
import {types} from "../../../../components/form/types";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {fileBlobFromAxios} from "../../../../components/form/form_inputs/form_file";
import {Page, PageHeader, Panel} from "../../../../components/kit";
import {PersonOverview, personName} from "./header";
import {PersonRolesPanel} from "./roles";

/**
 * Ficha de una persona (#13886): arriba quién es, sus cifras y los accesos a sus secciones (salarios,
 * ausencias, archivos, entrevistas…); debajo el formulario en bloques: datos personales (los mismos
 * campos que ve el empleado en «Mis datos»), datos privados de RR. HH. y costes.
 */
export const PersonEditPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(PersonRestControllerApiFactory);

	const [photoResponse, setPhotoResponse] = useState<AxiosResponse<string> | undefined>(undefined);
	const [summary, setSummary] = useState<PersonSummary>();

	const updatePhotoImg = (resp: AxiosResponse<Person>) => {
		if (resp.data && resp.data.id && resp.data.photo?.id) {
			apiManager
			.executeRaw(ctrl.viewPhotoPerson(resp.data.id))
			.then(p => setPhotoResponse(p));
		} else {
			setPhotoResponse(undefined);
		}
		return resp;
	}

	const form = useForm({
		fetcher: (id) => ctrl.getPerson(id).then(updatePhotoImg),
		updater: (bean) => ctrl.savePerson(bean).then(updatePhotoImg),
		remover: (id) => ctrl.deleteOrDismissPerson(id),
		factory: () => {
			return {} as Person;
		},
		idParamName: "personId"
	} as UseFormProps<Person>);

	const person = form.propModel.value;
	const personId = person?.id;

	// El resumen (salario vigente, vacaciones, última autoevaluación) es el que antes salía al pie.
	useEffect(() => {
		if (personId) {
			apiManager.execute(ctrl.summaryPerson(personId)).then(setSummary).catch(() => undefined);
		}
	}, [personId]);

	const [photoUrl, setPhotoUrl] = useState<string>();
	useEffect(() => {
		if (!photoResponse) {
			setPhotoUrl(undefined);
			return;
		}
		try {
			const {url} = fileBlobFromAxios(photoResponse);
			setPhotoUrl(url);
			return () => window.URL.revokeObjectURL(url);
		} catch (e) {
			setPhotoUrl(undefined);
		}
	}, [photoResponse]);

	const isNew = !form.isLoading() && !personId;

	return (
		<Page>
			<PageHeader back="/app/admin/employee/person"
						title={isNew ? "Nueva persona" : personName(person) || "Persona"}
						subtitle={isNew ? "Alta de una persona en la plantilla." : "Ficha de la persona"}/>

			{!isNew && <PersonOverview person={person} summary={summary} photoUrl={photoUrl}/>}
			{!isNew && <PersonRolesPanel personId={personId}/>}

			<FormContent form={form} withoutPaper>
				<Panel title="Datos personales" subtitle="Los mismos que la persona ve y puede cambiar en «Mis datos».">
					<EmployeeDataFields photoDownloader={() => ctrl.viewPhotoPerson(form.getIdOrErr())}/>
				</Panel>

				<Panel title="Datos privados" icon={<LockOutlinedIcon fontSize="small" color="action"/>}
					   subtitle="Solo RR. HH.: no los ve la persona." sx={{mt: 3}}>
					<FormInput type={"text"} field={"help"} label={"Ayuda para listado"} rows={1}/>

					<Grid container spacing={3}>
						<Grid item xs={12} sm={6} md={3}><FormSelect field={"calendar"} type={types.calendars} label={"Calendario"}/></Grid>
						<Grid item xs={12} sm={6} md={3}><FormInput field={"trainingStartDate"} type={"date"} label={"Inicio de prácticas"}/></Grid>
						<Grid item xs={12} sm={6} md={3}><FormInput field={"testEndDate"} type={"date"} label={"Fin del periodo de prueba"}/></Grid>
					</Grid>

					<Grid container spacing={3}>
						<Grid item xs={12} sm={6} md={3}>
							<FormSelect field={"workMode"} type={types.workModes} label={"Modalidad de trabajo"} displayEmpty
										helperText={"Vacía hasta que RR. HH. la confirme"}/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}><FormInput field={"deductionStartDate"} type={"date"} label={"Inicio de I+D+i"}/></Grid>
						<Grid item xs={12} sm={6} md={3}><FormInput field={"deductionEndDate"} type={"date"} label={"Fin de I+D+i"}/></Grid>
					</Grid>

					<Grid container spacing={3}>
						<Grid item xs={12} sm={6} md={3}><FormToggle field={"skipEvaluation"} label={"No evaluar"}/></Grid>
						<Grid item xs={12} sm={6} md={3}><FormToggle field={"skipMonthReminder"} label={"Sin aviso mensual"}/></Grid>
						<Grid item xs={12} sm={6} md={3}><FormToggle field={"inSocialSecurityRebate"} label={"Bonificable I+D+i"}/></Grid>
						<Grid item xs={12} sm={6} md={3}><FormToggle field={"inRiskPreventionPlan"} label={"En el plan de prevención"}/></Grid>
					</Grid>

					<Grid container spacing={3}>
						<Grid item xs={12} sm={4}><FormInput field={"developerStartDate"} type={"date"} label={"Programando desde"}/></Grid>
						<Grid item xs={12} sm={4}><FormInput field={"preventionTrainingDate"} type={"date"} label={"Última formación en prevención"}/></Grid>
						<Grid item xs={12} sm={4}><FormInput field={"jobEvaluationDate"} type={"date"} label={"Última evaluación del puesto"}/></Grid>
					</Grid>

					<FormInput type={"text"} field={"observation"} label={"Comentarios privados"} rows={7}/>
				</Panel>

				<Panel title="Costes" subtitle="Lo que cuesta dar de alta a la persona y mantenerla cada mes, además del salario."
					   sx={{mt: 3, mb: 1}}>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<FormInput type={"number"} field={"hiringCost"} label={"Costes de alta"} rows={1}/>
							<FormInput type={"text"} field={"hiringCostDesc"} label={"Descripción de los costes de alta"} rows={3}/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormInput type={"number"} field={"monthlyCost"} label={"Costes mensuales"} rows={1}/>
							<FormInput type={"text"} field={"monthlyCostDesc"} label={"Descripción de los costes mensuales"} rows={3}/>
						</Grid>
					</Grid>
				</Panel>
			</FormContent>
		</Page>
	);
};
